import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { loadEnMessages, viMessages } from '@/assets/i18n';
import {
  createTranslator,
  resolveLocale,
  type Locale,
  type Messages,
  type TFunction,
} from '@/core/i18n/translator';

const STORAGE_KEY = 'lct.locale';

interface I18nValue {
  locale: Locale;
  t: TFunction;
  setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18nValue | null>(null);

/** Saved locale lives in localStorage until the settings store lands (M3-15). Always guarded: storage may throw. */
function readSavedLocale(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function saveLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Private mode / blocked storage: the choice simply isn't remembered.
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() =>
    resolveLocale(window.location.search, readSavedLocale()),
  );
  const [en, setEn] = useState<Messages | undefined>(undefined);

  useEffect(() => {
    document.documentElement.lang = locale;
    if (locale === 'en' && !en) {
      void loadEnMessages().then(setEn);
    }
  }, [locale, en]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    saveLocale(next);
  }, []);

  const value = useMemo<I18nValue>(() => {
    const t = createTranslator({
      // Show Vietnamese for the instant before en.json arrives instead of warning about every key.
      locale: locale === 'en' && !en ? 'vi' : locale,
      messages: { vi: viMessages, en },
      onMissing: import.meta.env.DEV
        ? (key, loc) => {
            console.warn(`[i18n] missing "${key}" in ${loc}`);
          }
        : undefined,
    });
    return { locale, t, setLocale };
  }, [locale, en, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside <I18nProvider>');
  return value;
}
